import { useState, useCallback } from 'react';
import { apiService } from '../services/api.service';
import type { QuizAnswers, UserInfo, QuizSubmissionResponse, QuizStatusResponse } from '../types/api.types';

// Hook for quiz submission
export const useQuizSubmission = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submissionResult, setSubmissionResult] = useState<QuizSubmissionResponse | null>(null);

  const submitQuiz = useCallback(async (
    answers: QuizAnswers, 
    userInfo?: UserInfo, 
    sessionId?: string
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await apiService.submitQuiz(
        apiService.prepareQuizData(answers, userInfo, sessionId)
      );
      
      setSubmissionResult(result);
      return result;

    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to submit quiz';
      setError(errorMessage);
      throw err;

    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const resetSubmission = useCallback(() => {
    setError(null);
    setSubmissionResult(null);
  }, []);

  return {
    submitQuiz,
    isSubmitting,
    error,
    submissionResult,
    resetSubmission,
  };
};

// Hook for quiz status checking
export const useQuizStatus = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<QuizStatusResponse | null>(null);

  const checkStatus = useCallback(async (documentId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiService.getQuizStatus(documentId);
      setStatus(result);
      return result;

    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to check status';
      setError(errorMessage);
      throw err;

    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetStatus = useCallback(() => {
    setError(null);
    setStatus(null);
  }, []);

  return {
    checkStatus,
    isLoading,
    error,
    status,
    resetStatus,
  };
};

// Hook for API health check
export const useApiHealth = () => {
  const [isHealthy, setIsHealthy] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkHealth = useCallback(async () => {
    setIsChecking(true);
    setError(null);

    try {
      const result = await apiService.healthCheck();
      setIsHealthy(result.success);
      return result;

    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Health check failed';
      setError(errorMessage);
      setIsHealthy(false);
      throw err;

    } finally {
      setIsChecking(false);
    }
  }, []);

  return {
    checkHealth,
    isHealthy,
    isChecking,
    error,
  };
};

// Combined hook for complete quiz management
export const useQuizApi = () => {
  const quizSubmission = useQuizSubmission();
  const quizStatus = useQuizStatus();
  const apiHealth = useApiHealth();

  const submitAndTrackQuiz = useCallback(async (
    answers: QuizAnswers, 
    userInfo?: UserInfo, 
    sessionId?: string
  ) => {
    try {
      // Submit quiz
      const submission = await quizSubmission.submitQuiz(answers, userInfo, sessionId);
      
      // Check status after submission
      if (submission.documentId) {
        await quizStatus.checkStatus(submission.documentId);
      }

      return submission;

    } catch (error) {
      throw error;
    }
  }, [quizSubmission, quizStatus]);

  return {
    // Submission
    submitQuiz: quizSubmission.submitQuiz,
    submitAndTrackQuiz,
    isSubmitting: quizSubmission.isSubmitting,
    submissionError: quizSubmission.error,
    submissionResult: quizSubmission.submissionResult,
    resetSubmission: quizSubmission.resetSubmission,

    // Status
    checkStatus: quizStatus.checkStatus,
    isCheckingStatus: quizStatus.isLoading,
    statusError: quizStatus.error,
    status: quizStatus.status,
    resetStatus: quizStatus.resetStatus,

    // Health
    checkHealth: apiHealth.checkHealth,
    isHealthy: apiHealth.isHealthy,
    isCheckingHealth: apiHealth.isChecking,
    healthError: apiHealth.error,

    // Utility
    generateSessionId: apiService.generateSessionId,
  };
};
